import React, { useState } from 'react';
import { Modal, Button, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { BANGDIEM_LIENTUC_API } from '../../../../utils/apiUrl';
import { PostFormData } from '../../../../utils/httpProvider';

const ImportBangDiemLienTucModal = ({ show, onHide, lopId, onApply }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);

    const previewMutation = useMutation(
        (formData) => PostFormData(`${BANGDIEM_LIENTUC_API.IMPORT_EXCEL_PREVIEW}/${lopId}`, formData),
        {
            onSuccess: (res) => {
                const list = res?.$values || res || [];
                setPreviewData(list);
                const matchedCount = list.filter(x => x.isMatched).length;
                toast.success(`Đã đọc ${list.length} dòng dữ liệu, khớp ${matchedCount} học viên.`);
            },
            onError: (err) => {
                toast.error(err?.response?.data?.message || err?.message || "Lỗi khi đọc file Excel!");
            }
        }
    );

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            const formData = new FormData();
            formData.append('file', selectedFile);
            previewMutation.mutate(formData);
        }
    };

    const handleApply = () => {
        const matchedItems = previewData.filter(x => x.isMatched && x.hoSoTuyenSinhId);
        if (matchedItems.length === 0) {
            toast.warning("Không có bản ghi nào khớp với học viên trong lớp!");
            return;
        }

        if (onApply) {
            onApply(matchedItems);
        }
        toast.success(`Đã điền điểm cho ${matchedItems.length} học viên từ file Excel!`);
        handleClose();
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="text-primary font-weight-bold" style={{ fontSize: '18px' }}>
                    <i className="fas fa-file-excel mr-2 text-success"></i>
                    Nhập Điểm Từ File Excel
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="small mb-3">
                    <i className="fas fa-info-circle mr-1"></i>
                    Hệ thống sẽ tự động nhận diện các cột: <strong>TT, Họ và tên, Điểm lý thuyết, Điểm thực hành, Điểm trung bình, Xếp loại, Kết quả</strong>.
                </Alert>

                <div className="mb-3">
                    <label className="font-weight-bold mb-1">Chọn file Excel bảng điểm (.xlsx, .xls):</label>
                    <input
                        type="file"
                        className="form-control"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        disabled={previewMutation.isLoading}
                    />
                </div>

                {previewMutation.isLoading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <div className="mt-2 text-muted">Đang phân tích dữ liệu file Excel...</div>
                    </div>
                )}

                {previewData.length > 0 && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="font-weight-bold">
                                Kết quả đối soát ({previewData.filter(x => x.isMatched).length}/{previewData.length} học viên khớp):
                            </span>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '350px' }}>
                            <Table bordered hover size="sm" className="mb-0 text-nowrap">
                                <thead className="thead-light">
                                    <tr>
                                        <th>STT</th>
                                        <th>Họ tên trong file</th>
                                        <th>Khớp học viên</th>
                                        <th>Điểm LT</th>
                                        <th>Điểm TH</th>
                                        <th>Điểm TB</th>
                                        <th>Xếp loại</th>
                                        <th>Kết quả</th>
                                        <th>Trạng thái</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((item, idx) => (
                                        <tr key={idx} className={item.isMatched ? "" : "table-danger"}>
                                            <td>{item.stt || idx + 1}</td>
                                            <td className="font-weight-bold">{item.hoTenFile}</td>
                                            <td>{item.hoTenKhop || <span className="text-muted">—</span>}</td>
                                            <td>{item.diemLyThuyet ?? "—"}</td>
                                            <td>{item.diemThucHanh ?? "—"}</td>
                                            <td className="font-weight-bold">{item.diemTrungBinh ?? "—"}</td>
                                            <td>{item.xepLoai || "—"}</td>
                                            <td>
                                                {item.isDat === true && <Badge variant="success">Đạt</Badge>}
                                                {item.isDat === false && <Badge variant="danger">Không đạt</Badge>}
                                                {item.isDat === null && <span className="text-muted">—</span>}
                                            </td>
                                            <td>
                                                {item.isMatched ? (
                                                    <Badge variant="success"><i className="fas fa-check mr-1"></i>Khớp</Badge>
                                                ) : (
                                                    <Badge variant="danger"><i className="fas fa-times mr-1"></i>Không khớp</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Đóng
                </Button>
                <Button
                    variant="primary"
                    onClick={handleApply}
                    disabled={previewData.filter(x => x.isMatched).length === 0 || previewMutation.isLoading}
                >
                    <i className="fas fa-check mr-1"></i> Áp dụng vào bảng điểm
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImportBangDiemLienTucModal;
